/**
 * API client for sensor endpoints and water tests
 */

import type { SensorDataResponse, WaterTestResponse } from '@/types/api';
import { buildApiUrl } from './config';
import { apiClient } from './client';

interface GetSensorDataParams {
  tank_id: string;
  start_date: string;
  end_date: string;
  parameters?: string[];
  exclude_outliers?: boolean;
}

export const api = {
  /**
   * Fetch sensor data from the API
   */
  async getSensorData(params: GetSensorDataParams): Promise<SensorDataResponse> {
    const url = new URL(buildApiUrl(`/api/tanks/${params.tank_id}/sensors/data`), window.location.origin);

    // Add query parameters
    url.searchParams.append('start_date', params.start_date);
    url.searchParams.append('end_date', params.end_date);

    if (params.parameters) {
      params.parameters.forEach(p => {
        url.searchParams.append('parameters', p);
      });
    }

    if (params.exclude_outliers !== undefined) {
      url.searchParams.append('exclude_outliers', String(params.exclude_outliers));
    }

    const response = await apiClient(url.toString(), {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Trigger manual data refresh
   */
  async triggerManualRefresh(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient(buildApiUrl('/api/refresh/trigger'), {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Fetch water test data from the API
   */
  async getWaterTestData(params: {
    tank_id: string;
    start_date: string;
    end_date: string;
    param_name?: string;
    device?: string;
    exclude_outliers?: boolean;
  }): Promise<WaterTestResponse> {
    const url = new URL(buildApiUrl(`/api/tanks/${params.tank_id}/water-tests/data`), window.location.origin);

    // Add query parameters
    url.searchParams.append('start_date', params.start_date);
    url.searchParams.append('end_date', params.end_date);

    if (params.param_name) {
      url.searchParams.append('param_name', params.param_name);
    }

    if (params.device) {
      url.searchParams.append('device', params.device);
    }

    if (params.exclude_outliers !== undefined) {
      url.searchParams.append('exclude_outliers', String(params.exclude_outliers));
    }

    const response = await apiClient(url.toString(), {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }
};
